// add-slide/tidy-spacers.ts
//
// SPACING THAT BELONGED TO SOMETHING GOES WITH IT.
//
// A library layout spaces its elements with Separators: icon, spacer,
// heading; picture, spacer, name. The pipeline removes elements it cannot
// fill (an unfilled picture, a counter with no figure, a logo slot with no
// brand), and until now it left their spacers behind: a card that starts with
// a gap where its icon was, a card that ends spacer, divider, spacer. Dawid
// (2026-10-11): "There are a lot of unnecessary spacers (for example one after
// another)".
//
// Not one layout in the library puts two Separators next to each other, and
// only a handful start or end a group with one (a card's inner padding). So a
// removal takes the spacer it leaves exposed at the edge of its group, and two
// spacers meeting in the middle become one — while a layout's own padding,
// which no removal touched, stays where the designer put it.

type Node = {
  type?: string;
  settings?: Record<string, unknown>;
  children?: unknown;
};

const isSpacer = (node: unknown): boolean =>
  (node as Node | null)?.settings?.name === "Separator";

// What a removal can leave stranded at the edge of a group: a spacer, or a
// divider that now divides nothing from something.
const isGap = (node: unknown): boolean =>
  isSpacer(node) || (node as Node | null)?.settings?.name === "Divider";

const heightOf = (node: unknown): number => {
  const h = Number((node as Node | null)?.settings?.height);
  return Number.isFinite(h) ? h : 0;
};

/**
 * Removes `items` from `children` in place, and the spacing they leave
 * behind: a spacer or divider newly exposed at either end of the group, and
 * two spacers that now meet (the taller one stays). Returns how many nodes
 * were removed.
 */
export const spliceWithSpacing = (
  children: unknown[],
  items: readonly unknown[],
): number => {
  if (items.length === 0) return 0;
  const before = children.length;
  // The group's own edge padding: a spacer that started or ended the group
  // before anything was removed is the layout's, and stays.
  const ownEdges = new Set<unknown>(
    [children[0], children[children.length - 1]].filter(isGap),
  );
  const drop = new Set(items);
  const kept = children.filter((child) => !drop.has(child));
  if (kept.length === before) return 0;

  const out: unknown[] = [];
  for (const child of kept) {
    const last = out[out.length - 1];
    if (isSpacer(child) && isSpacer(last)) {
      // Two spacers meeting where something used to be: one gap, the taller.
      if (heightOf(child) > heightOf(last)) out[out.length - 1] = child;
      continue;
    }
    out.push(child);
  }
  while (out.length > 0 && isGap(out[0]) && !ownEdges.has(out[0])) {
    out.shift();
  }
  while (
    out.length > 0 &&
    isGap(out[out.length - 1]) &&
    !ownEdges.has(out[out.length - 1])
  ) {
    out.pop();
  }
  children.splice(0, children.length, ...out);
  return before - out.length;
};

/**
 * `slide` with its spacers tidied: two spacers that meet anywhere become one
 * (the library never puts them together, so a pair is always left over), and
 * a Separator's stray `content` — the library ships "3. High spacer (80px)"
 * inside 183 of them, text no reader sees but every pass that reads a slide's
 * words does — is dropped.
 *
 * Returns the SAME object when nothing changed.
 */
export const tidySpacers = (
  slide: unknown,
): { slide: unknown; changed: number } => {
  if (!slide || typeof slide !== "object") return { slide, changed: 0 };
  let changed = 0;
  const next = structuredClone(slide) as Node;
  const visit = (node: Node) => {
    if (!node || typeof node !== "object") return;
    if (isSpacer(node) && node.settings && "content" in node.settings) {
      delete node.settings.content;
      changed++;
    }
    if (!Array.isArray(node.children)) return;
    const children = node.children as Node[];
    for (let i = children.length - 1; i > 0; i--) {
      if (isSpacer(children[i]) && isSpacer(children[i - 1])) {
        if (heightOf(children[i]) > heightOf(children[i - 1])) {
          children[i - 1] = children[i];
        }
        children.splice(i, 1);
        changed++;
      }
    }
    for (const child of children) visit(child);
  };
  visit(next);
  return changed > 0 ? { slide: next, changed } : { slide, changed: 0 };
};
